/**
 * 前台浏览访问上报。
 *
 * 作用：把「谁（账号）在什么时间看了哪个页面」记到后端 edu_visit_log，
 *       后端在管理端「访问记录」页里可以按账号 / IP / 时间 / 页面查询。
 *
 * 为什么放前端上报：这是 SPA，页面切换（hash 路由）不会产生新的服务端请求，
 * 服务端只能看到一次 `/` 文档请求，拿不到用户真正看了哪一篇。
 *
 * 发送时机：
 *   1. 每次路由切换 → 上报新页面，并回填上一页的停留时长（touch）
 *   2. 页面隐藏 / 关闭 → 用 sendBeacon 回填当前页停留时长
 *
 * IP / User-Agent / 时间由服务端补全（前端拿到的 IP 不可信）。
 * 后台管理端自身的路由（/admin）不上报。
 */
import { API_BASE } from './apiBase';

const VISITOR_KEY = 'edu_visitor_id';
const REPORT_URL = API_BASE + '/visit/api/report';

/** 需要临时关闭上报时：.env.* 里设 VUE_APP_VISIT_DISABLED=true（后端 Visit:Enabled=false 也能整体关掉） */
const DISABLED = String(process.env.VUE_APP_VISIT_DISABLED || '').toLowerCase() === 'true';

/** 这些前缀的路由不上报（后台自身） */
const SKIP_PREFIXES = ['/admin'];

/** 同一路由在这么短的时间内重复触发，视为重复上报 */
const DEDUP_MS = 1500;

/** 停留时长小于这个值就不回填了（快速划过不算） */
const MIN_STAY_MS = 1000;

let last = { path: '', at: 0 };
let leaveBound = false;

/** 访客标识：本地生成并持久化，用于区分新老访客 */
function visitorId() {
    try {
        let v = window.localStorage.getItem(VISITOR_KEY);
        if (!v) {
            v = 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
            window.localStorage.setItem(VISITOR_KEY, v);
        }
        return v;
    } catch (e) {
        return '';
    }
}

/** 从登录态里取账号信息（不同接入方式存的键不一样，逐个兜底） */
function accountInfo() {
    const ls = window.localStorage;
    const pick = keys => {
        for (let i = 0; i < keys.length; i++) {
            const v = ls.getItem(keys[i]);
            if (v) {
                return v;
            }
        }
        return '';
    };
    return {
        account: pick(['loginUserName', 'uname', 'userName', 'userId']),
        nickname: pick(['userName', 'uname']),
        role: ls.getItem('role') || ''
    };
}

function screenSize() {
    try {
        return window.screen ? window.screen.width + 'x' + window.screen.height : '';
    } catch (e) {
        return '';
    }
}

function buildPayload(path, extra) {
    const info = accountInfo();
    const payload = {
        path: path,
        title: extra.title || '',
        code: extra.code || '',
        account: info.account,
        nickname: info.nickname,
        role: info.role,
        visitor: visitorId(),
        screen: screenSize(),
        referer: document.referrer || ''
    };
    if (extra.stayMs) {
        payload.stayMs = extra.stayMs;
    }
    if (extra.touch) {
        payload.touch = true;
    }
    return payload;
}

/** 用 XHR/axios 上报（页面还活着的时候） */
function post(payload) {
    const url = REPORT_URL;
    const body = JSON.stringify(payload);
    try {
        if (window.fetch) {
            // keepalive 让请求在页面卸载时也能发出去（跨域默认就是 cors 模式，无需显式指定）
            window.fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: body,
                keepalive: true
            }).catch(() => {});
            return;
        }
    } catch (e) {
        /* 回落到 XHR */
    }
    try {
        const xhr = new window.XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(body);
    } catch (e) {
        /* 上报失败不影响页面 */
    }
}

/** 用 sendBeacon 上报（页面正在关闭时唯一可靠的方式） */
function beacon(payload) {
    try {
        if (!window.navigator || !window.navigator.sendBeacon) {
            post(payload);
            return;
        }
        const blob = new window.Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = window.navigator.sendBeacon(REPORT_URL, blob);
        if (!ok) {
            post(payload);
        }
    } catch (e) {
        /* 忽略 */
    }
}

/** 取出路由对应的上报路径（带 query，便于区分带参进入） */
function routePath(route) {
    if (!route) {
        return '';
    }
    if (route.fullPath) {
        // hash 模式下 fullPath 已经是 /xxx?y=z 形式
        return route.fullPath;
    }
    return route.path || '';
}

function skipped(path) {
    for (let i = 0; i < SKIP_PREFIXES.length; i++) {
        if (path.indexOf(SKIP_PREFIXES[i]) === 0) {
            return true;
        }
    }
    return false;
}

/** 页面隐藏 / 关闭时，把当前页的停留时长补上 */
function flushStay(useBeacon) {
    if (DISABLED || !last.path || !last.at) {
        return;
    }
    const stay = Date.now() - last.at;
    if (stay < MIN_STAY_MS) {
        return;
    }
    const payload = buildPayload(last.path, { stayMs: stay, touch: true });
    if (useBeacon) {
        beacon(payload);
    } else {
        post(payload);
    }
    // 记完就重置起点，避免重复回填
    last.at = Date.now();
}

/**
 * 路由切换上报：先回填上一页停留时长，再上报新页面。
 * @param {object} route vue-router 的 to 对象
 */
export function reportVisit(route) {
    try {
        if (DISABLED) {
            return;
        }
        const path = routePath(route);
        if (!path || skipped(path)) {
            return;
        }
        const now = Date.now();

        // 回填上一页停留
        if (last.path && last.path !== path) {
            const stay = now - last.at;
            if (stay >= MIN_STAY_MS) {
                post(buildPayload(last.path, { stayMs: stay, touch: true }));
            }
        }

        // 去重：同一路径极短时间内重复触发
        if (last.path === path && now - last.at < DEDUP_MS) {
            return;
        }

        const meta = (route && route.meta) || {};
        post(
            buildPayload(path, {
                title: meta.title || (typeof document !== 'undefined' ? document.title : ''),
                code: meta.code || ''
            })
        );

        last = { path: path, at: now };
    } catch (e) {
        /* 上报失败不影响页面 */
    }
}

/**
 * 挂上路由与页面生命周期监听。在 router 创建之后调用一次即可。
 * @param {import('vue-router').default} router
 */
export function setupVisitTracker(router) {
    if (router && typeof router.afterEach === 'function') {
        router.afterEach(to => {
            // 等页面标题更新完再上报，这样 title 更准确
            window.setTimeout(() => reportVisit(to), 0);
        });
    }

    if (leaveBound) {
        return;
    }
    leaveBound = true;

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushStay(true);
        } else {
            last.at = Date.now();
        }
    });
    window.addEventListener('pagehide', () => flushStay(true));
    window.addEventListener('beforeunload', () => flushStay(true));
}

export default { reportVisit, setupVisitTracker };
